import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import WebSocketService from '../services/WebSocketService'
import { type WebSocketMessage, WebSocketStatus } from '../types/websocket.types'
import { useAuth } from 'react-oidc-context'

interface WebSocketContextProps {
  webSocketService: WebSocketService | null
  status: WebSocketStatus
  messages: WebSocketMessage[]
  connect: (url: string, tenantId?: string, username?: string) => void
  disconnect: () => void
  subscribeToDevice: (deviceId: string) => Promise<WebSocketMessage>
  subscribeToDeviceKPIs: (deviceId: string) => Promise<WebSocketMessage>
  subscribeToDeviceAlarms: (deviceId: string) => Promise<WebSocketMessage>
  subscribeToDeviceStatus: (deviceId: string) => Promise<WebSocketMessage>
  subscribeToAssetModBus: (modBusPayload: object) => Promise<WebSocketMessage>
  subscribeToAssetKPIs: (KPIPayload: object) => Promise<WebSocketMessage>
  subscribeToAlertDurationStatus: (deviceId: string) => Promise<WebSocketMessage>
  clearMessages: () => void
  isConnected: boolean
  authData: { tenantId: string, username: string, isAuthenticated: boolean }
}

interface WebSocketProviderProps {
  children: ReactNode
}

const WebSocketContext = createContext<WebSocketContextProps>({
  webSocketService: null,
  status: WebSocketStatus.DISCONNECTED,
  messages: [],
  connect: (): void => {
    // Default implementation - will be overridden by provider
  },
  disconnect: (): void => {
    // Default implementation - will be overridden by provider
  },
  subscribeToDevice: async (): Promise<WebSocketMessage> => await Promise.reject(new Error('WebSocket not initialized')),
  subscribeToDeviceKPIs: async (): Promise<WebSocketMessage> => await Promise.reject(new Error('WebSocket not initialized')),
  subscribeToDeviceAlarms: async (): Promise<WebSocketMessage> => await Promise.reject(new Error('WebSocket not initialized')),
  subscribeToDeviceStatus: async (): Promise<WebSocketMessage> => await Promise.reject(new Error('WebSocket not initialized')),
  subscribeToAssetModBus: async (): Promise<WebSocketMessage> => await Promise.reject(new Error('WebSocket not initialized')),
  subscribeToAssetKPIs: async (): Promise<WebSocketMessage> => await Promise.reject(new Error('WebSocket not initialized')),
  subscribeToAlertDurationStatus: async (): Promise<WebSocketMessage> => await Promise.reject(new Error('WebSocket not initialized')),
  clearMessages: (): void => {
    // Default implementation - will be overridden by provider
  },
  isConnected: false,
  authData: { tenantId: '', username: '', isAuthenticated: false }
})

export const useWebSocket = (): WebSocketContextProps => useContext(WebSocketContext)

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const auth = useAuth()
  const [webSocketService, setWebSocketService] = useState<WebSocketService | null>(null)
  const [status, setStatus] = useState<WebSocketStatus>(WebSocketStatus.DISCONNECTED)
  const [messages, setMessages] = useState<WebSocketMessage[]>([])

  // Get auth data
  const authData = {
    tenantId: (auth.user?.profile?.customer as string) ?? '',
    username: (auth.user?.profile?.name as string) ?? '',
    isAuthenticated: auth.isAuthenticated
  }

  // Clean up WebSocket connection when component unmounts
  useEffect(() => {
    return (): void => {
      if (webSocketService !== null) {
        webSocketService.disconnect()
      }
    }
  }, [webSocketService])

  const connect = (url: string, tenantId?: string, username?: string): void => {
    // Use provided values or fall back to auth data
    const finalTenantId = (tenantId !== null && tenantId !== undefined && tenantId !== '') ? tenantId : (authData.tenantId !== '' ? authData.tenantId : '')
    const finalUsername = (username !== null && username !== undefined && username !== '') ? username : (authData.username !== '' ? authData.username : '')

    // Disconnect existing connection if any
    if (webSocketService !== null) {
      webSocketService.disconnect()
    }

    // Set connecting status immediately for better UI feedback
    setStatus(WebSocketStatus.CONNECTING)

    const newWebSocketService = new WebSocketService({
      url,
      tenantId: finalTenantId,
      username: finalUsername,
      onStatusChange: (newStatus: WebSocketStatus) => {
        setStatus(newStatus)
      },
      onMessage: (message: WebSocketMessage) => {
        setMessages((prevMessages) => {
          // Check for duplicate correlationId
          if (message.correlationId !== null && message.correlationId !== undefined &&
              prevMessages.some(msg => msg.correlationId === message.correlationId)) {
            return prevMessages // Don't add duplicate
          }
          return [...prevMessages, message]
        })
      },
      onOpen: () => {
        // WebSocket connection opened
      },
      onError: (error: Event) => {
        console.error('WebSocket error:', error)
      }
    })

    setWebSocketService(newWebSocketService)
    newWebSocketService.connect()
  }

  const disconnect = (): void => {
    if (webSocketService !== null) {
      webSocketService.disconnect()
      setWebSocketService(null)
    }
  }

  const subscribeToDevice = async (deviceId: string): Promise<WebSocketMessage> => {
    if (webSocketService === null) {
      return await Promise.reject(new Error('WebSocket not connected'))
    }
    return await webSocketService.subscribeToDevice(deviceId)
  }

  const subscribeToDeviceKPIs = async (deviceId: string): Promise<WebSocketMessage> => {
    if (webSocketService === null) {
      return await Promise.reject(new Error('WebSocket not connected'))
    }
    return await webSocketService.subscribeToDeviceKPIs(deviceId)
  }

  const subscribeToDeviceAlarms = async (deviceId: string): Promise<WebSocketMessage> => {
    if (webSocketService === null) {
      return await Promise.reject(new Error('WebSocket not connected'))
    }
    return await webSocketService.subscribeToDeviceAlarms(deviceId)
  }

  const subscribeToDeviceStatus = async (deviceId: string): Promise<WebSocketMessage> => {
    if (webSocketService === null) {
      return await Promise.reject(new Error('WebSocket not connected'))
    }
    return await webSocketService.subscribeToDeviceStatus(deviceId)
  }

  const subscribeToAlertDurationStatus = async (deviceId: string): Promise<WebSocketMessage> => {
    if (webSocketService === null) {
      return await Promise.reject(new Error('WebSocket not connected'))
    }
    return await webSocketService.subscribeToAlertDurationStatus(deviceId)
  }

  const subscribeToAssetModBus = async (modBusPayload: any): Promise<WebSocketMessage> => {
    if (webSocketService === null) {
      return await Promise.reject(new Error('WebSocket not connected'))
    }
    return await webSocketService.subscribeToAssetModBus(modBusPayload)
  }

  const subscribeToAssetKPIs = async (KPIPayload: any): Promise<WebSocketMessage> => {
    if (webSocketService === null) {
      return await Promise.reject(new Error('WebSocket not connected'))
    }
    return await webSocketService.subscribeToAssetKPIs(KPIPayload)
  }

  const clearMessages = (): void => {
    setMessages([])
  }

  const isConnected = webSocketService !== null ? webSocketService.isConnected() : false

  const value = {
    webSocketService,
    status,
    messages,
    connect,
    disconnect,
    subscribeToDevice,
    subscribeToDeviceKPIs,
    subscribeToDeviceAlarms,
    subscribeToDeviceStatus,
    subscribeToAssetModBus,
    subscribeToAssetKPIs,
    subscribeToAlertDurationStatus,
    clearMessages,
    isConnected,
    authData
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

export default WebSocketProvider
